import { formatKey, kindName, suggestFormat } from "@ondit/publishing";
import { Button, Label } from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import { useAccess } from "~/core/access/index";
import { ApiError, api } from "~/core/api/index";
import { useI18n, useT } from "~/core/i18n";
import { brandContracts } from "~/core/publishing";
import { keys, read } from "~/core/query";
import { Hint } from "~/core/ui/page";
import { SearchSelect } from "~/core/ui/search-select";
import { Async } from "~/core/ui/states";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";
import { PlanModal, PublishingCalendar } from "~/modules/analytics/features/platform-publishing/calendar";
import { dayKey } from "~/modules/analytics/features/platform-publishing/calendar-data";
import { PublishingContentEditor } from "~/modules/analytics/features/platform-publishing/content-editor";
import { useExtensionReceipts } from "~/modules/analytics/features/platform-publishing/history";
import { articleMarkdown } from "~/modules/analytics/lib/distribution";
import { PublisherConnection } from "../features/platform-publishing/install";

export function DistributionPage({ entityId, filters, setFilters }: ReadingProps) {
	const t = useT();
	const { locale } = useI18n();
	const { editor } = useAccess();
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "distribution"],
		queryFn: () => api.distribution(entityId),
		...read,
	});
	// Only the titles are read here, so only the language is asked for: the
	// same list the publishing screens hold.
	const pages = useQuery({
		queryKey: [...keys.entity(entityId), "pages", filters.locale],
		queryFn: () => api.pages(entityId, { locale: filters.locale }),
		...read,
	});
	const [params, setParams] = useSearchParams();
	const taskId = params.get("task");
	const tasks = useQuery({
		queryKey: [...keys.entity(entityId), "platform-tasks"],
		queryFn: () => api.platformTasks(entityId),
		enabled: !!taskId,
	});

	const [page, setPage] = useState(params.get("page") ?? "");
	const [platform, setPlatform] = useState(params.get("platform") ?? "");
	// The kind of post on a platform that takes several; "" until somebody
	// picks, which reads as what the article most naturally becomes.
	const [format, setFormat] = useState(params.get("format") ?? "");
	const selectedPlatform = state.data?.platforms.find((p) => p.id === platform);
	const contracts = brandContracts(state.data?.platforms);
	const methods = contracts.byId(platform)?.methods;
	useExtensionReceipts(entityId);
	const [tab, setTab] = useState(params.get("tab") === "prepare" || params.get("page") ? "prepare" : "calendar");
	const [finishing, setFinishing] = useState(false);
	const [editing, setEditing] = useState(!!params.get("page") && !!params.get("platform"));
	const [busy, setBusy] = useState(false);
	const [planning, setPlanning] = useState(false);
	const [recommendations, setRecommendations] = useState<
		{ platform_id: string; reason: string; cited_answers: number }[]
	>([]);
	const source = useQuery({
		queryKey: [...keys.entity(entityId), "distribution-source", page],
		queryFn: () => api.distributionSource(entityId, page),
		enabled: page !== "",
		...read,
	});
	const base = contracts.byId(platform);
	const kind =
		base &&
		(format ||
			(source.data &&
				suggestFormat(base, {
					length: articleMarkdown(source.data).length,
					video: source.data.blocks.some((b) => b.type === "video"),
					images: source.data.blocks.filter((b) => b.type === "image").length,
				})) ||
			"");
	const planned = state.data?.posts.find(
		(p) => p.page_id === page && p.platform_id === platform && (!base || p.format === formatKey(base, kind || null)),
	);
	// The recommendations are for the article they were asked about, and no
	// other: picking a different one clears them rather than leaving five
	// platforms reasoned about a page that is no longer on screen.
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset on the article, not on every render
	useEffect(() => setRecommendations([]), [entityId, page]);

	/*
	 * Only from the button. A recommendation is a model call, and one that fired
	 * from an effect on every article picked spent credits on articles the
	 * person was only scrolling past to reach the one they meant.
	 */
	async function recommend() {
		if (!page || busy) return;
		setBusy(true);
		try {
			const result = await api.recommendPlatforms(entityId, page);
			setRecommendations(result.recommendations);
		} catch (error) {
			toast.error(error instanceof ApiError ? error.localized(locale) : String(error));
		} finally {
			setBusy(false);
		}
	}

	return (
		<StageShell
			entityId={entityId}
			reading="distribution"
			filters={filters}
			setFilters={setFilters}
			lede={t("publishFlow.intro")}
			actions={
				// Outlined: the calendar's own "Plan a post" is this screen's filled action.
				tab === "calendar" && editor ? (
					<Button
						variant="default"
						onClick={() => {
							setParams({ tab: "prepare" });
							setPage("");
							setPlatform("");
							setFormat("");
							setEditing(false);
							setFinishing(false);
							setTab("prepare");
						}}
					>
						{t("publishFlow.publishNow")}
					</Button>
				) : undefined
			}
		>
			{!(tab === "prepare" && editing && page && platform) && <PublisherConnection />}
			{/* Publishing is a page the calendar, "Publish now" and the rows elsewhere
			    lead to, not a tab: it is a task started from an article, and the tabs
			    are places to read. */}
			{tab === "prepare" ? (
				taskId && tasks.isPending ? (
					<Async state={tasks}>{() => null}</Async>
				) : tasks.error && taskId ? (
					<Async state={tasks}>{() => null}</Async>
				) : (
					<div className="publisher-flow">
						<ol className="publisher-steps" aria-label={t("publishFlow.steps")}>
							<li aria-current={!editing ? "step" : undefined}>
								<span>1</span>
								{t("publishFlow.choose")}
							</li>
							<li aria-current={editing && !finishing ? "step" : undefined}>
								<span>2</span>
								{t("publishFlow.check")}
							</li>
							<li aria-current={finishing ? "step" : undefined}>
								<span>3</span>
								{t("publishFlow.publish")}
							</li>
						</ol>
						{editing && selectedPlatform ? (
							<>
								<div className="publisher-selection">
									<div>
										<strong>{pages.data?.find((p) => p.id === page)?.title}</strong>
										<p className="hint">
											{base ? kindName(base, kind || null, locale) : selectedPlatform.name}
											{planned?.planned_at
												? ` · ${t("publishFlow.plannedFor", { time: new Date(planned.planned_at).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" }) })}`
												: null}
										</p>
									</div>
									<div className="row-actions">
										{/* Every platform can be put on the calendar, including the
									    ones the assistant cannot publish unattended: a planned
									    day is a reminder, not a schedule. */}
										<Button variant="ghost" disabled={!editor} onClick={() => setPlanning(true)}>
											{t(planned?.planned_at ? "publishCalendar.setTime" : "publishFlow.plan")}
										</Button>
										<Button
											variant="ghost"
											onClick={() => {
												setEditing(false);
												setFinishing(false);
											}}
										>
											{t("publishFlow.changeSelection")}
										</Button>
									</div>
								</div>
								<Async state={source}>
									{(article) =>
										article && (
											<PublishingContentEditor
												key={`${entityId}:${article.id}:${article.revision}:${platform}`}
												source={article}
												format={kind || undefined}
												onFormatChange={(next) => {
													setFormat(next);
													setParams(
														(old) => {
															old.set("format", next);
															old.delete("task");
															return old;
														},
														{ replace: true },
													);
												}}
												onStageChange={setFinishing}
												platform={selectedPlatform}
												entityId={entityId}
											/>
										)
									}
								</Async>
								{planning && (
									<PlanModal
										entityId={entityId}
										day={dayKey(new Date())}
										post={{
											page,
											platform,
											format: base ? formatKey(base, kind || null) : "",
											title: pages.data?.find((p) => p.id === page)?.title ?? "",
											plannedAt: planned?.planned_at ?? null,
										}}
										pages={pages.data ?? []}
										platforms={state.data?.platforms ?? []}
										onClose={() => setPlanning(false)}
									/>
								)}
							</>
						) : (
							<section className="publisher-choose">
								<h3>{t("publishFlow.chooseHeading")}</h3>
								<div className="publisher-choices">
									<Async state={pages}>
										{(items) => (
											<div className="field">
												<Label>{t("publishFlow.article")}</Label>
												<SearchSelect
													value={page}
													onChange={setPage}
													options={items
														.filter((p) => p.has_live_version)
														.map((p) => ({ value: p.id, label: p.title }))}
													label={t("publishFlow.article")}
													placeholder={t("distribution.chooseArticle")}
													searchLabel={t("table.search")}
													emptyLabel={t("table.noMatch")}
												/>
												{!items.some((p) => p.has_live_version) && <p>{t("publishFlow.noArticle")}</p>}
											</div>
										)}
									</Async>
									<Async state={state}>
										{(data) => (
											<div className="field">
												<Label>{t("publishFlow.destination")}</Label>
												<SearchSelect
													value={platform}
													onChange={(next) => {
														setPlatform(next);
														setFormat("");
													}}
													options={data.platforms.map((p) => ({ value: p.id, label: p.name, keywords: p.domain }))}
													label={t("publishFlow.destination")}
													placeholder={t("publishFlow.selectPlatform")}
													searchLabel={t("distribution.search")}
													emptyLabel={t("table.noMatch")}
												/>
											</div>
										)}
									</Async>
								</div>
								{selectedPlatform && (
									<p className="hint">
										{t(
											methods?.includes("api")
												? methods.includes("browser")
													? "publishFlow.bothMethods"
													: "publishFlow.apiMethod"
												: methods?.includes("browser")
													? "publishFlow.browserMethod"
													: "publishFlow.manualMethod",
										)}
									</p>
								)}
								<div className="platform-actions">
									<Button
										disabled={!page || !selectedPlatform}
										onClick={() => {
											setParams({ tab: "prepare", page, platform }, { replace: true });
											setEditing(true);
										}}
									>
										{t("publishFlow.continue")}
									</Button>
									<Button variant="ghost" disabled={!page || busy || !editor} onClick={recommend}>
										{busy ? t("distribution.recommending") : t("publishFlow.recommend")}
									</Button>
									<Hint text={t("distribution.recommendHint")} />
								</div>
								{recommendations.length > 0 && (
									<div className="publisher-recommendations">
										{recommendations.map((r) => (
											<div key={r.platform_id}>
												<Button
													variant="ghost"
													onClick={() => {
														setPlatform(r.platform_id);
														setFormat("");
													}}
												>
													{state.data?.platforms.find((p) => p.id === r.platform_id)?.name}
												</Button>
												<p className="hint">{r.reason}</p>
											</div>
										))}
									</div>
								)}
							</section>
						)}
					</div>
				)
			) : (
				<Async state={state}>
					{(data) => (
						<Async state={pages}>
							{(articles) => (
								<PublishingCalendar
									entityId={entityId}
									posts={data.posts}
									platforms={data.platforms}
									pages={articles}
									onEdit={(page, platform, task, format) => {
										setParams({
											tab: "prepare",
											page,
											platform,
											...(format ? { format } : {}),
											...(task ? { task } : {}),
										});
										setPage(page);
										setPlatform(platform);
										setFormat(format ?? "");
										setEditing(true);
										setFinishing(false);
										setTab("prepare");
									}}
								/>
							)}
						</Async>
					)}
				</Async>
			)}
		</StageShell>
	);
}
